import React from "react";

import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useSearchParams } from "react-router-dom";

import { Module, useModules } from "src/api/modulesApi";
import { useUserOrganizations, useWorkspaces } from "src/api/queries";
import { oauthApi } from "src/api/taktileApi";
import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { FormItem } from "src/base-components/FormItem";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Option, Section, Select } from "src/base-components/Select";
import {
  ModuleKind,
  WorkspaceDataplane,
  WorkspaceStatus,
} from "src/clients/flow-api";
import { OAuthConsentRequest, OrganizationInDB } from "src/clients/taktile-api";
import { Callout } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import {
  RecentWorkspace,
  useRecentWorkspaces,
} from "src/utils/useRecentWorkspaces";

type ConsentError = {
  error: string;
  error_description: string;
};

const WORKSPACE_STATUS_TOOLTIPS: Record<
  WorkspaceStatus,
  { variant: PillVariants; label: string } | null
> = {
  [WorkspaceStatus.SUCCESS]: null, // Don't display tooltip for succeeded workspaces
  [WorkspaceStatus.PENDING]: {
    variant: "indigo-light",
    label: "Update Pending",
  },
  [WorkspaceStatus.FAILURE]: { variant: "yellow", label: "Update Failed" },
};

const WorkspaceOptionLabel: React.FC<{ ws: WorkspaceDataplane }> = ({ ws }) => {
  const tooltip = WORKSPACE_STATUS_TOOLTIPS[ws.status];
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{ws.name}</span>
      {tooltip && (
        <Pill size="sm" variant={tooltip.variant}>
          <Pill.Text>{tooltip.label}</Pill.Text>
        </Pill>
      )}
    </span>
  );
};

const getEligibleWorkspaces = (
  workspaces: WorkspaceDataplane[] | undefined,
  organizations: OrganizationInDB[] | undefined,
  cpModules: Module[] | undefined,
  modulesError: boolean,
): WorkspaceDataplane[] => {
  if (!workspaces || !organizations) return [];
  if (!modulesError && !cpModules) return [];

  const orgById = Object.fromEntries(
    organizations.map((org) => [org.id, org] as const),
  );

  // When the modules fetch failed, skip the cp_proxy filter so
  // workspaces are still shown rather than an empty picker.
  const cpEnabledWsIds = cpModules
    ? new Set(
        cpModules
          .filter((m) => m.spec !== null && m.spec.is_active)
          .map((m) => m.workspaceId),
      )
    : null;

  return workspaces
    .filter(
      (ws) =>
        // base_url is optional and absent for not-yet-provisioned
        // workspaces, so its presence is what gates eligibility here
        Boolean(ws.base_url) &&
        orgById[ws.organization_id] &&
        (cpEnabledWsIds === null || cpEnabledWsIds.has(ws.id)),
    )
    .sort((a, b) => {
      const orgCompare = (orgById[a.organization_id]?.name ?? "").localeCompare(
        orgById[b.organization_id]?.name ?? "",
      );
      if (orgCompare !== 0) return orgCompare;
      return a.name.localeCompare(b.name);
    });
};

export const OAuthConsentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id");
  const clientId = searchParams.get("client_id");

  const { data: organizations, isLoading: orgsLoading } =
    useUserOrganizations();
  const { data: workspaces, isLoading: wsLoading } = useWorkspaces();
  const {
    data: cpModules,
    isLoading: modulesLoading,
    isError: modulesError,
  } = useModules({
    kind: ModuleKind.CP_PROXY,
    pageLimit: 1000,
  });
  const { recentWorkspaces, addRecentWorkspace } = useRecentWorkspaces();

  const eligibleWorkspaces = getEligibleWorkspaces(
    workspaces,
    organizations,
    cpModules,
    modulesError,
  );

  const orgById = Object.fromEntries(
    (organizations ?? []).map((org) => [org.id, org] as const),
  );

  const sections: Section[] = [
    ...new Set(eligibleWorkspaces.map((ws) => ws.organization_id)),
  ].map((orgId) => ({
    key: orgId,
    displayValue: orgById[orgId]?.name ?? orgId,
  }));

  const options: Option[] = eligibleWorkspaces.map((ws) => ({
    key: ws.id,
    value: <WorkspaceOptionLabel ws={ws} />,
    displayValue: ws.name,
    searchText: `${ws.name} ${orgById[ws.organization_id]?.name ?? ""} ${
      WORKSPACE_STATUS_TOOLTIPS[ws.status]?.label ?? ""
    }`,
    sectionKey: ws.organization_id,
  }));

  const activeWsIds = new Set(eligibleWorkspaces.map((ws) => ws.id));
  const validRecentWorkspaces = recentWorkspaces.filter((rw) =>
    activeWsIds.has(rw.workspaceId),
  );

  const [chosenWsId, setChosenWsId] = React.useState<string | null>(null);

  const chosenWs =
    eligibleWorkspaces.find((ws) => ws.id === chosenWsId) ?? null;
  const chosenOrg = chosenWs
    ? (orgById[chosenWs.organization_id] ?? null)
    : null;

  const mutation = useMutation({
    mutationFn: (body: OAuthConsentRequest) =>
      oauthApi.consentApiV1OauthConsentPost(body),
    onSuccess: (response) => {
      window.location.href = response.data.redirect_url;
    },
  });

  const errorMessage =
    mutation.error instanceof AxiosError
      ? ((mutation.error.response?.data as ConsentError | undefined)
          ?.error_description ?? "An unexpected error occurred")
      : mutation.isError
        ? "An unexpected error occurred"
        : undefined;

  if (!requestId || !clientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Callout variant="error">
          Invalid authorization request. Missing required parameters.
        </Callout>
      </div>
    );
  }

  const isLoading = orgsLoading || wsLoading || modulesLoading;
  const canAuthorize = Boolean(chosenWs);

  const handleSelectWorkspace = (wsId: string) => {
    setChosenWsId(wsId);
    trackRecent(wsId);
  };

  const trackRecent = (wsId: string) => {
    const ws = eligibleWorkspaces.find((w) => w.id === wsId);
    const org = ws ? orgById[ws.organization_id] : undefined;
    if (ws && org) {
      addRecentWorkspace({
        workspaceId: ws.id,
        workspaceName: ws.name,
        orgId: org.id,
        orgName: org.name,
      });
    }
  };

  const handleRecentClick = (recent: RecentWorkspace) => {
    setChosenWsId(recent.workspaceId);
    addRecentWorkspace(recent);
  };

  const handleAuthorize = () => {
    const body: OAuthConsentRequest = {
      request_id: requestId,
      ...(chosenOrg &&
        chosenWs && {
          org_id: chosenOrg.id,
          ws_id: chosenWs.id,
          workspace_slug: chosenWs.slug,
          workspace_name: chosenWs.name,
          workspace_base_url: chosenWs.base_url,
          principal: chosenOrg.principal,
          org_name: chosenOrg.name,
        }),
    };
    mutation.mutate(body);
  };

  const handleDeny = () => {
    mutation.mutate({ request_id: requestId, denied: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-[400px]">
        <Card variant="inverted">
          <Card.Header className="mb-1 p-2 pb-0">
            <Card.Title title={`Authorize ${clientId}`} />
          </Card.Header>
          <div className="space-y-4 p-2">
            <p className="font-inter-normal-13px text-gray-500">
              Select a workspace to authorize CLI access.
            </p>

            {isLoading ? (
              <div className="space-y-3">
                <Skeleton height="h-8" />
                <Skeleton height="h-8" />
              </div>
            ) : (
              <>
                {validRecentWorkspaces.length > 0 && (
                  <FormItem gap={false}>
                    <FormItem.Label>
                      <span className="mr-1 inline-flex">
                        <Icon icon={faClockRotateLeft} size="xs" />
                      </span>
                      Recent
                    </FormItem.Label>
                    <div className="flex flex-wrap gap-2">
                      {validRecentWorkspaces.map((recent) => (
                        <Button
                          key={recent.workspaceId}
                          dataLoc={`recent-workspace-${recent.workspaceId}`}
                          variant={
                            chosenWsId === recent.workspaceId
                              ? "primary"
                              : "secondary"
                          }
                          size="sm"
                          onClick={() => handleRecentClick(recent)}
                        >
                          {recent.orgName} | {recent.workspaceName}
                        </Button>
                      ))}
                    </div>
                  </FormItem>
                )}

                <FormItem gap={false} label="Workspace">
                  <Select
                    searchable
                    dataLoc="workspace-select"
                    options={options}
                    placeholder="Search and select a workspace..."
                    searchPlaceholder="Search workspaces..."
                    sectionDividers
                    sections={sections}
                    value={chosenWsId}
                    onChange={handleSelectWorkspace}
                  />
                </FormItem>
              </>
            )}

            {errorMessage !== undefined && (
              <Callout variant="error">{errorMessage}</Callout>
            )}

            <div className="flex gap-3">
              <Button
                htmlType="button"
                variant="primary"
                loading={mutation.isPending && !mutation.variables?.denied}
                disabled={mutation.isPending || !canAuthorize}
                onClick={handleAuthorize}
                fullWidth
              >
                Authorize
              </Button>
              <Button
                htmlType="button"
                variant="secondary"
                loading={
                  mutation.isPending && mutation.variables?.denied === true
                }
                disabled={mutation.isPending}
                onClick={handleDeny}
                fullWidth
              >
                Deny
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
